<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useAlert } from "dashboard/composables";
import { useAdmin } from "dashboard/composables/useAdmin";
import ConversationAPI from "dashboard/api/inbox/conversation";
import KanbanSettingsModal from "../components/KanbanSettingsModal.vue";
import {
  buildPipelineKey,
  matchesKanbanFilters,
  matchesKanbanSearch,
  moveConversationBetweenColumns,
  normalizeConversation,
  pipelineBoards,
  payloadConversations,
  serializeFlowMetadata,
  sortKanbanConversations,
} from "../helpers";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();

const PAGE_SIZE = 25;
const MAX_PAGES_PER_STAGE = 100;
const AUTO_REFRESH_MS = 30000;
let autoRefreshTimer;

const loading = ref(true);
const refreshing = ref(false);
const movingId = ref(null);
const draggedConversation = ref(null);
const search = ref("");
const selectedInboxId = ref("all");
const selectedAssigneeId = ref("all");
const selectedPriority = ref("all");
const sortBy = ref("recent");
const selectedBoardKey = ref("");
const columns = ref([]);
const settingsOpen = ref(false);
const settingsCreating = ref(false);
const savingSettings = ref(false);

const accountId = computed(() => Number(route.params.accountId));
const inboxes = computed(() => store.getters["inboxes/getInboxes"] || []);
const conversationAttributes = computed(
  () => store.getters["attributes/getConversationAttributes"] || [],
);
const boards = computed(() => pipelineBoards(conversationAttributes.value));
const activeBoard = computed(
  () =>
    boards.value.find((board) => board.key === selectedBoardKey.value) ||
    boards.value[0] ||
    null,
);
const stages = computed(
  () => activeBoard.value?.metadata?.stages?.map((stage) => stage.name) || [],
);
const boardInboxIds = computed(
  () => activeBoard.value?.metadata?.inboxIds || [],
);
const availableInboxes = computed(() => {
  if (!boardInboxIds.value.length) return inboxes.value;
  return inboxes.value.filter((inbox) =>
    boardInboxIds.value.includes(Number(inbox.id)),
  );
});
const totalCards = computed(() =>
  visibleColumns.value.reduce(
    (total, column) => total + column.conversations.length,
    0,
  ),
);
const columnCounts = computed(() =>
  Object.fromEntries(
    columns.value.map((column) => [column.stage, column.conversations.length]),
  ),
);
const assignees = computed(() => {
  const unique = new Map();
  columns.value.forEach((column) => {
    column.conversations.forEach((conversation) => {
      if (conversation.assigneeId && conversation.assigneeName) {
        unique.set(Number(conversation.assigneeId), conversation.assigneeName);
      }
    });
  });
  return [...unique.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name, "pt-BR"));
});
const hasActiveFilters = computed(
  () =>
    Boolean(search.value.trim()) ||
    selectedInboxId.value !== "all" ||
    selectedAssigneeId.value !== "all" ||
    selectedPriority.value !== "all",
);

const visibleColumns = computed(() =>
  columns.value.map((column) => ({
    ...column,
    conversations: sortKanbanConversations(
      column.conversations.filter((conversation) => {
        const boardMatches =
          !boardInboxIds.value.length ||
          boardInboxIds.value.includes(Number(conversation.inbox_id));
        return (
          boardMatches &&
          matchesKanbanFilters(conversation, {
            inboxId: selectedInboxId.value,
            assigneeId: selectedAssigneeId.value,
            priority: selectedPriority.value,
          }) &&
          matchesKanbanSearch(conversation, search.value)
        );
      }),
      sortBy.value,
    ),
  })),
);

const toneByColor = {
  blue: "bg-n-blue-9",
  amber: "bg-n-amber-9",
  violet: "bg-n-violet-9",
  teal: "bg-n-teal-9",
  green: "bg-n-green-9",
  ruby: "bg-n-ruby-9",
  slate: "bg-n-slate-9",
};

const stageConfig = (stage) =>
  activeBoard.value?.metadata?.stages?.find((item) => item.name === stage) ||
  {};
const columnTone = (stage, index) =>
  toneByColor[stageConfig(stage).color] ||
  Object.values(toneByColor)[index % Object.values(toneByColor).length];

const requestStage = async (stage, { unassigned = false } = {}) => {
  const filter = {
    attribute_key: activeBoard.value.key,
    filter_operator: unassigned ? "is_not_present" : "equal_to",
    values: unassigned ? [] : [stage],
    query_operator: null,
  };

  const conversations = [];
  for (let page = 1; page <= MAX_PAGES_PER_STAGE; page += 1) {
    const response = await ConversationAPI.filter({
      page,
      queryData: { payload: [filter] },
    });
    const batch = payloadConversations(response);
    conversations.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }

  return conversations.map(normalizeConversation);
};

const loadBoard = async ({ quiet = false } = {}) => {
  if (quiet) refreshing.value = true;
  else loading.value = true;

  try {
    await Promise.all([
      store.dispatch("attributes/get"),
      store.dispatch("inboxes/get"),
    ]);

    if (!selectedBoardKey.value && boards.value.length) {
      selectedBoardKey.value = boards.value[0].key;
    }

    if (!activeBoard.value || !stages.value.length) {
      columns.value = [];
      return;
    }

    const loaded = await Promise.all(
      stages.value.map(async (stage, index) => {
        const conversations = await requestStage(stage);
        if (index === 0) {
          const unassigned = await requestStage(stage, { unassigned: true });
          const existing = new Set(
            conversations.map((item) => Number(item.id)),
          );
          conversations.push(
            ...unassigned.filter((item) => !existing.has(Number(item.id))),
          );
        }
        return { stage, conversations };
      }),
    );
    columns.value = loaded;
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        "Não foi possível carregar o Kanban.",
    );
  } finally {
    loading.value = false;
    refreshing.value = false;
  }
};

const onDragStart = (conversation) => {
  draggedConversation.value = conversation;
};

const onDragEnd = () => {
  draggedConversation.value = null;
};

const moveConversation = async (conversation, targetStage) => {
  if (!conversation) return;

  const currentStage =
    conversation.custom_attributes?.[activeBoard.value.key] || stages.value[0];
  if (currentStage === targetStage) return;

  const previousColumns = columns.value;
  movingId.value = conversation.id;
  columns.value = moveConversationBetweenColumns(
    columns.value,
    conversation.id,
    targetStage,
  );

  try {
    const customAttributes = {
      ...(conversation.custom_attributes || {}),
      [activeBoard.value.key]: targetStage,
    };
    await ConversationAPI.updateCustomAttributes({
      conversationId: conversation.id,
      customAttributes,
    });
    conversation.custom_attributes = customAttributes;
    useAlert(`Lead movido para “${targetStage}”.`);
  } catch (error) {
    columns.value = previousColumns;
    useAlert(
      error?.response?.data?.message ||
        "Não foi possível mover o lead. A alteração foi desfeita.",
    );
  } finally {
    movingId.value = null;
  }
};

const onDrop = async (targetStage) => {
  const conversation = draggedConversation.value;
  draggedConversation.value = null;
  await moveConversation(conversation, targetStage);
};

const onQuickMove = async (conversation, event) => {
  event.stopPropagation();
  await moveConversation(conversation, event.target.value);
};

const clearFilters = () => {
  search.value = "";
  selectedInboxId.value = "all";
  selectedAssigneeId.value = "all";
  selectedPriority.value = "all";
};

const openConversation = (conversation) => {
  router.push({
    name: "inbox_conversation",
    params: {
      accountId: accountId.value,
      conversation_id: conversation.id,
    },
  });
};

const openColumnSettings = ({ creating = false } = {}) => {
  settingsCreating.value = creating;
  settingsOpen.value = true;
};

const migrateRenamedStages = async (metadata) => {
  const renames = metadata.stages.filter(
    (stage) => stage.originalName && stage.originalName !== stage.name,
  );
  for (const stage of renames) {
    const source = columns.value.find(
      (column) => column.stage === stage.originalName,
    );
    for (const conversation of source?.conversations || []) {
      await ConversationAPI.updateCustomAttributes({
        conversationId: conversation.id,
        customAttributes: {
          ...(conversation.custom_attributes || {}),
          [activeBoard.value.key]: stage.name,
        },
      });
    }
  }
};

const saveBoardSettings = async ({ name, metadata }) => {
  savingSettings.value = true;
  try {
    const cleanMetadata = {
      ...metadata,
      stages: metadata.stages.map(({ originalName, ...stage }) => stage),
    };
    if (settingsCreating.value) {
      const key = buildPipelineKey(
        name,
        conversationAttributes.value.map((attribute) => attribute.attributeKey),
      );
      await store.dispatch("attributes/create", {
        attribute_display_name: name,
        attribute_description: serializeFlowMetadata(cleanMetadata),
        attribute_display_type: 6,
        attribute_key: key,
        attribute_model: "conversation_attribute",
        attribute_values: cleanMetadata.stages.map((stage) => stage.name),
      });
      selectedBoardKey.value = key;
    } else {
      await store.dispatch("attributes/update", {
        id: activeBoard.value.id,
        attribute_display_name: name,
        attribute_description: serializeFlowMetadata(cleanMetadata),
        attribute_values: cleanMetadata.stages.map((stage) => stage.name),
      });
      await migrateRenamedStages(metadata);
    }
    settingsOpen.value = false;
    await loadBoard();
    useAlert(settingsCreating.value ? "Funil criado." : "Funil atualizado.");
  } catch (error) {
    useAlert(error?.message || "Não foi possível salvar o funil.");
  } finally {
    savingSettings.value = false;
  }
};

const deleteBoard = async (board) => {
  savingSettings.value = true;
  try {
    await store.dispatch("attributes/delete", board.id);
    selectedBoardKey.value = "";
    settingsOpen.value = false;
    await loadBoard();
    useAlert("Funil excluído.");
  } catch (error) {
    useAlert(error?.message || "Não foi possível excluir o funil.");
  } finally {
    savingSettings.value = false;
  }
};

const switchBoard = () => {
  clearFilters();
  loadBoard();
};

const autoRefresh = () => {
  if (
    document.visibilityState === "visible" &&
    !loading.value &&
    !refreshing.value &&
    !movingId.value &&
    !settingsOpen.value
  ) {
    loadBoard({ quiet: true });
  }
};

const onVisibilityChange = () => {
  if (document.visibilityState === "visible") autoRefresh();
};

const formatActivity = (timestamp) => {
  if (!timestamp) return "";
  const value =
    Number(timestamp) < 1000000000000
      ? Number(timestamp) * 1000
      : Number(timestamp);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const priorityLabel = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Média",
  low: "Baixa",
};

const priorityTone = {
  urgent: "bg-n-ruby-3 text-n-ruby-11",
  high: "bg-n-amber-3 text-n-amber-11",
  medium: "bg-n-blue-3 text-n-blue-11",
  low: "bg-n-slate-3 text-n-slate-11",
};

onMounted(() => {
  loadBoard();
  autoRefreshTimer = window.setInterval(autoRefresh, AUTO_REFRESH_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
});

onBeforeUnmount(() => {
  window.clearInterval(autoRefreshTimer);
  document.removeEventListener("visibilitychange", onVisibilityChange);
});
</script>

<template>
  <main
    class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-slate-1 text-n-slate-12"
  >
    <header
      class="flex flex-col gap-4 border-b border-n-weak bg-n-background px-4 py-4 sm:px-6 xl:flex-row xl:items-center xl:justify-between"
    >
      <div class="flex min-w-0 items-center gap-3">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
        >
          <i class="i-lucide-columns-3 size-5" />
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h1 class="truncate text-lg font-semibold">
              {{ activeBoard?.name || "Avant Flow" }}
            </h1>
            <span
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
            >
              {{ totalCards }} leads
            </span>
          </div>
          <p class="mt-0.5 text-sm text-n-slate-10">
            {{
              activeBoard?.metadata?.description ||
              "Acompanhe e mova as conversas sem sair do atendimento."
            }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div v-if="boards.length" class="flex items-center gap-1">
          <select
            v-model="selectedBoardKey"
            aria-label="Funil"
            class="h-9 min-w-40 rounded-lg border border-n-weak bg-n-background px-3 text-sm font-medium outline-none focus:border-n-brand"
            @change="switchBoard"
          >
            <option v-for="board in boards" :key="board.key" :value="board.key">
              {{ board.name }}
            </option>
          </select>
          <button
            v-if="isAdmin"
            type="button"
            class="inline-flex size-9 items-center justify-center rounded-lg border border-n-weak bg-n-background hover:bg-n-alpha-2"
            title="Criar outro funil"
            @click="openColumnSettings({ creating: true })"
          >
            <i class="i-lucide-plus size-4" />
          </button>
        </div>
        <label class="relative min-w-52">
          <i
            class="i-lucide-search absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-9"
          />
          <input
            v-model="search"
            type="search"
            placeholder="Buscar lead ou telefone"
            class="h-9 w-full rounded-lg border border-n-weak bg-n-background pl-9 pr-3 text-sm outline-none focus:border-n-brand"
          />
        </label>
        <select
          v-model="selectedInboxId"
          aria-label="Filtrar por caixa"
          class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand"
        >
          <option value="all">Todas as caixas</option>
          <option
            v-for="inbox in availableInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <select
          v-model="selectedAssigneeId"
          aria-label="Filtrar por responsável"
          class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand"
        >
          <option value="all">Todos responsáveis</option>
          <option value="unassigned">Sem responsável</option>
          <option
            v-for="assignee in assignees"
            :key="assignee.id"
            :value="assignee.id"
          >
            {{ assignee.name }}
          </option>
        </select>
        <select
          v-model="selectedPriority"
          aria-label="Filtrar por prioridade"
          class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand"
        >
          <option value="all">Todas prioridades</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="medium">Média</option>
          <option value="low">Baixa</option>
          <option value="none">Sem prioridade</option>
        </select>
        <select
          v-model="sortBy"
          aria-label="Ordenar cards"
          class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand"
        >
          <option value="recent">Mais recentes</option>
          <option value="oldest">Há mais tempo parado</option>
          <option value="priority">Maior prioridade</option>
        </select>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="inline-flex size-9 items-center justify-center rounded-lg border border-n-weak bg-n-background hover:bg-n-alpha-2"
          title="Limpar filtros"
          aria-label="Limpar filtros"
          @click="clearFilters"
        >
          <i class="i-lucide-filter-x size-4" />
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-background px-3 text-sm font-medium hover:bg-n-alpha-2"
          @click="openColumnSettings()"
        >
          <i class="i-lucide-settings-2 size-4" />
          Configurar funil
        </button>
        <button
          type="button"
          class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-background px-3 text-sm font-medium hover:bg-n-alpha-2 disabled:cursor-wait disabled:opacity-60"
          :disabled="refreshing"
          @click="loadBoard({ quiet: true })"
        >
          <i
            class="i-lucide-refresh-cw size-4"
            :class="{ 'animate-spin': refreshing }"
          />
          Atualizar
        </button>
      </div>
    </header>

    <section v-if="loading" class="flex flex-1 items-center justify-center">
      <div class="flex items-center gap-3 text-sm text-n-slate-10">
        <i class="i-lucide-loader-circle size-5 animate-spin" />
        Carregando pipeline…
      </div>
    </section>

    <section
      v-else-if="!activeBoard"
      class="flex flex-1 items-center justify-center p-6"
    >
      <div
        class="max-w-lg rounded-xl border border-n-weak bg-n-background p-6 text-center shadow-sm"
      >
        <div
          class="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-n-amber-3 text-n-amber-11"
        >
          <i class="i-lucide-list-filter size-6" />
        </div>
        <h2 class="text-base font-semibold">Crie o primeiro funil</h2>
        <p class="mt-2 text-sm leading-6 text-n-slate-10">
          Defina as etapas e as caixas de entrada. A configuração será
          compartilhada automaticamente com a equipe.
        </p>
        <button
          v-if="isAdmin"
          type="button"
          class="mt-4 inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:bg-n-brand/90"
          @click="openColumnSettings({ creating: true })"
        >
          <i class="i-lucide-plus size-4" />
          Criar funil
        </button>
      </div>
    </section>

    <section v-else class="flex-1 overflow-x-auto overflow-y-hidden p-4 sm:p-6">
      <div class="flex h-full min-w-max gap-4">
        <article
          v-for="(column, index) in visibleColumns"
          :key="column.stage"
          class="flex h-full w-[min(310px,calc(100vw-3rem))] flex-col overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
          @dragover.prevent
          @drop="onDrop(column.stage)"
        >
          <header
            class="flex items-center justify-between border-b border-n-weak px-3 py-3"
          >
            <div class="flex min-w-0 items-center gap-2">
              <span
                class="size-2.5 shrink-0 rounded-full"
                :class="columnTone(column.stage, index)"
              />
              <h2
                class="truncate text-sm font-semibold"
                :title="stageConfig(column.stage).description || column.stage"
              >
                {{ column.stage }}
              </h2>
              <span
                v-if="stageConfig(column.stage).type !== 'open'"
                class="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                :class="
                  stageConfig(column.stage).type === 'won'
                    ? 'bg-n-green-3 text-n-green-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
              >
                {{
                  stageConfig(column.stage).type === "won" ? "ganho" : "perdido"
                }}
              </span>
            </div>
            <span
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-10"
            >
              {{ column.conversations.length
              }}<template v-if="hasActiveFilters"
                >/{{ columnCounts[column.stage] || 0 }}</template
              >
            </span>
          </header>

          <div class="flex-1 space-y-2 overflow-y-auto p-2.5">
            <div
              v-for="conversation in column.conversations"
              :key="conversation.id"
              role="button"
              tabindex="0"
              draggable="true"
              class="group block w-full cursor-grab rounded-lg border border-n-weak bg-n-background p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-n-strong hover:shadow active:cursor-grabbing disabled:opacity-60"
              :class="{
                'pointer-events-none opacity-60': movingId === conversation.id,
              }"
              @dragstart="onDragStart(conversation)"
              @dragend="onDragEnd"
              @click="openConversation(conversation)"
              @keydown.enter="openConversation(conversation)"
              @keydown.space.prevent="openConversation(conversation)"
            >
              <div class="flex items-start gap-2.5">
                <img
                  v-if="conversation.avatarUrl"
                  :src="conversation.avatarUrl"
                  alt=""
                  class="size-8 shrink-0 rounded-full object-cover"
                />
                <span
                  v-else
                  class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs font-semibold text-n-slate-11"
                >
                  {{ conversation.contactName.slice(0, 2).toUpperCase() }}
                </span>
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <p class="truncate text-sm font-medium">
                      {{ conversation.contactName }}
                    </p>
                    <div class="flex shrink-0 items-center gap-1.5">
                      <span
                        v-if="conversation.priority"
                        class="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                        :class="priorityTone[conversation.priority]"
                      >
                        {{ priorityLabel[conversation.priority] }}
                      </span>
                      <span class="text-[11px] text-n-slate-9"
                        >#{{ conversation.display_id }}</span
                      >
                    </div>
                  </div>
                  <p
                    v-if="conversation.preview && !activeBoard.metadata.compact"
                    class="mt-1 line-clamp-2 text-xs leading-5 text-n-slate-10"
                  >
                    {{ conversation.preview }}
                  </p>
                </div>
              </div>

              <div
                v-if="!activeBoard.metadata.compact"
                class="mt-3 flex items-center justify-between gap-2 border-t border-n-weak pt-2 text-[11px] text-n-slate-9"
              >
                <span class="flex min-w-0 items-center gap-1">
                  <i class="i-lucide-inbox size-3" />
                  <span class="truncate">{{
                    conversation.inboxName || "Caixa"
                  }}</span>
                </span>
                <span>{{ formatActivity(conversation.last_activity_at) }}</span>
              </div>
              <div
                v-if="
                  conversation.assigneeName && !activeBoard.metadata.compact
                "
                class="mt-1.5 flex items-center gap-1 text-[11px] text-n-slate-9"
              >
                <i class="i-lucide-user-round size-3" />
                <span class="truncate">{{ conversation.assigneeName }}</span>
              </div>
              <div
                class="mt-2 flex items-center justify-between gap-2"
                @click.stop
              >
                <span
                  v-if="
                    conversation.labels.length && !activeBoard.metadata.compact
                  "
                  class="truncate text-[11px] text-n-slate-9"
                >
                  {{ conversation.labels.slice(0, 2).join(" · ") }}
                </span>
                <span v-else />
                <label class="relative shrink-0">
                  <span class="sr-only">Mover para outra etapa</span>
                  <select
                    :value="column.stage"
                    class="h-7 max-w-36 rounded-md border border-n-weak bg-n-background pl-2 pr-6 text-[11px] font-medium outline-none hover:border-n-strong focus:border-n-brand"
                    :disabled="movingId === conversation.id"
                    title="Mover para outra etapa"
                    @change="onQuickMove(conversation, $event)"
                  >
                    <option v-for="stage in stages" :key="stage" :value="stage">
                      {{ stage }}
                    </option>
                  </select>
                </label>
              </div>
            </div>

            <div
              v-if="!column.conversations.length"
              class="flex min-h-28 items-center justify-center rounded-lg border border-dashed border-n-weak px-4 text-center text-xs leading-5 text-n-slate-9"
            >
              Arraste uma conversa para esta etapa.
            </div>
          </div>
        </article>
      </div>
    </section>

    <KanbanSettingsModal
      v-if="settingsOpen"
      :board="settingsCreating ? null : activeBoard"
      :inboxes="inboxes"
      :counts="columnCounts"
      :busy="savingSettings"
      @close="settingsOpen = false"
      @save="saveBoardSettings"
      @delete="deleteBoard"
    />
  </main>
</template>
